// SceneryPreviewBaker — Set13 P6: the VARIANT PREVIEW STRIP's bake helper.
//
// The kind editor (in `TerrainSceneries_ModUI`, which lives in the DOM sidebar OUTSIDE the R3F
// canvas) needs to show the baked atlas cells side by side — "are my 6 variants actually
// different?" at a glance (§5 P6). The real render bake happens inside `SceneryRenderer` on the
// R3F canvas's renderer; this module re-derives it on an OFF-FRAME, throwaway WebGL2 renderer so
// the preview never disturbs the visible frame or the map's own context.
//
// DESIGN:
//   - Lazily creates ONE throwaway WebGL2 context + three.js WebGLRenderer, the same recipe the
//     GPU test lane uses (`Scenery.gpu.test.ts`). Cached across previews.
//   - `bakeKindPreview` returns per-cell data-URLs + coverage (the bake's `pixels` is a plain
//     CPU-side Uint8ClampedArray, so converting to `<img>`-friendly data URLs is trivial).
//   - Runs in the DOM sidebar, so it uses a plain DOM `<canvas>` for the ImageData → data-URL
//     step (`canvas.toDataURL`), NOT the OffscreenCanvas used by the worker/GPU lane.
//   - DEGRADES GRACEFULLY (§8's "degrade, never throw"): if WebGL2 / canvas is unavailable, or
//     the bake throws, it returns `null` — the UI shows a placeholder instead of crashing. The
//     preview is an authoring affordance, never a correctness path.
import * as THREE from "three";
import {ImpostorBaker, kindTextureTypeIDs, type BakeLightRig, type SceneryBakeResult} from "./ImpostorBaker.js";
import {preloadTextures, texturesReady} from "./SceneryTextures.js";
import type {ResolvedSceneryKind} from "../SceneryResolverCore.js";
import {applyOutputTransfer} from "./OutputTransfer.js";

let renderer: THREE.WebGLRenderer | null = null;
let baker: ImpostorBaker | null = null;
let previewCanvas: HTMLCanvasElement | null = null;
let previewCtx: CanvasRenderingContext2D | null = null;

/** Lazily acquire the preview's throwaway WebGL renderer + a DOM 2D canvas for data-URL encoding.
 *  Returns false when unavailable. */
function ensureResources(): boolean {
	if (renderer) return true;
	// DOM only (this module is imported from the sidebar, never the worker).
	if (typeof document === "undefined" || typeof document.createElement !== "function") return false;
	try {
		const canvas = document.createElement("canvas");
		canvas.width = 1; canvas.height = 1;
		const gl = canvas.getContext("webgl2", {alpha: true, depth: true, stencil: false, antialias: false, preserveDrawingBuffer: true});
		if (gl == null) return false;
		renderer = new THREE.WebGLRenderer({context: gl, canvas});
		renderer.setClearColor(0, 0);
		baker = new ImpostorBaker(renderer, {cellSize: 128});
		previewCanvas = document.createElement("canvas");
		previewCtx = previewCanvas.getContext("2d");
		return previewCtx != null;
	} catch {
		return false;
	}
}

/** Bake a kind and convert each cell to a data URL + coverage. Returns null when WebGL is
 *  unavailable or the bake fails (the preview then shows a placeholder — never throws).
 *  Set14 P4 §4.9 — the kind's textures must be READY before baking (the bake's MeshStandardMaterial
 *  reads them; a bake taken before they arrived would be a flat untextured preview that the
 *  signature then caches as final). The caller (KindEditor) re-invokes when readiness flips; this
 *  function kicks off the preload and returns null until ready. */
export function bakeKindPreview(resolved: ResolvedSceneryKind, light: BakeLightRig): {cells: string[]; coverage: number[]; bake: SceneryBakeResult} | null {
	if (!ensureResources()) return null;
	// §4.9 — preload the kind's texture sets (idempotent); return null until they're ready so a
	// pre-texture bake is never produced (and thus never cached). The caller re-bakes on readiness.
	const {barkTypes, leafTypes} = kindTextureTypeIDs(resolved);
	if (!texturesReady(barkTypes, leafTypes)) {
		void preloadTextures(barkTypes, leafTypes);
		return null;
	}
	try {
		const bake = baker!.bakeKind(resolved, light);
		if (bake == null) return null;
		// Top-left-origin RGBA bytes (the baker's layout) → per-cell data URLs.
		const {pixels, cols, cellSize, atlasWidth, variants, coverage} = bake;
		const cells: string[] = [];
		for (let i = 0; i < variants.length; i++) {
			const cellCol = i % cols;
			const cellRow = Math.floor(i / cols);
			const img = new ImageData(cellSize, cellSize);
			const dst = img.data;
			for (let y = 0; y < cellSize; y++) {
				for (let x = 0; x < cellSize; x++) {
					const srcX = cellCol * cellSize + x;
					const srcY = cellRow * cellSize + y;
					const sOff = (srcY * atlasWidth + srcX) * 4;
					const dOff = (y * cellSize + x) * 4;
					// Set14 P7 §4.11 REVISED — the atlas pixels are LINEAR (three forces
					// NoToneMapping + linear output for the bake's render target), but the `<img>`
					// the browser renders here is interpreted as sRGB. So apply the SAME output
					// transfer the tree quad shader applies at its final display write
					// (tone-map + sRGB-encode), or the preview stays dark and no longer matches
					// the map. RGB only — alpha passes through untouched (matches the shader).
					const [r, g, b] = applyOutputTransfer([pixels[sOff], pixels[sOff + 1], pixels[sOff + 2]]);
					dst[dOff] = r;
					dst[dOff + 1] = g;
					dst[dOff + 2] = b;
					dst[dOff + 3] = pixels[sOff + 3];
				}
			}
			previewCanvas!.width = cellSize;
			previewCanvas!.height = cellSize;
			previewCtx!.putImageData(img, 0, 0);
			cells.push(previewCanvas!.toDataURL("image/png"));
		}
		return {cells, coverage, bake};
	} catch {
		return null;
	}
}
